import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { mapCandidateAttachments } from 'features/sendouts/common/utils';
import { useShowAlert } from 'hooks';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import API from 'services/API';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';

import { checkIfCandidateIsAlreadyInSendout } from '../components/drawers/CreateSendoutDrawer/SendoutForm/SendoutForm.utils';
import {
  getCandidateRecruitersOptions,
  getDefaultCandidateRecruiter
} from '../components/shared/CandidateDetailsFields/CandidateDetailsFields.utils';

const CANDIDATE_HYDRATION_INCLUDES = [
  'additionalRecruiters',
  'filesForSendouts',
  'personalInformation',
  'recruiter'
].join(',');

const getEmptyDuplicateInfo = () => ({
  isDuplicate: false,
  duplicates: [],
  error: null,
  shouldBlock: false
});

export const useSendoutCandidateHydration = ({
  jobOrderId,
  onCandidateInfoChange = () => {},
  onDuplicateChange = () => {}
}) => {
  const { showAlert } = useShowAlert();
  const [candidateAttachments, setCandidateAttachments] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState(getEmptyDuplicateInfo());
  const [hydratedCandidateId, setHydratedCandidateId] = useState(null);
  const [isHydratingCandidate, setIsHydratingCandidate] = useState(false);
  const latestRequestIdRef = useRef(0);
  const { nextSignal, abort } = useLatestRequestSignal();

  const hydrateCandidate = useCallback(
    async ({ candidateId, disableDuplicateBlocking = false }) => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      if (!candidateId) {
        abort();
        setCandidateAttachments([]);
        setRecruiterOptions([]);
        setDuplicateInfo(getEmptyDuplicateInfo());
        setHydratedCandidateId(null);
        setIsHydratingCandidate(false);
        onCandidateInfoChange(null);
        onDuplicateChange(getEmptyDuplicateInfo());

        return {
          attachments: [],
          duplicateInfo: getEmptyDuplicateInfo(),
          error: null,
          recruiterOptions: [],
          resolvedRecruiter: null
        };
      }

      try {
        setIsHydratingCandidate(true);
        const signal = nextSignal();

        const candidateResponse = await API.get(`${Endpoints.Candidates}/${candidateId}`, {
          apiVersion: 2,
          signal,
          params: {
            includes: CANDIDATE_HYDRATION_INCLUDES
          }
        });

        if (candidateResponse.status !== HTTPStatusCodes.Ok) {
          throw new Error('Candidate details request failed');
        }

        const candidateData = candidateResponse.data;
        const attachments = mapCandidateAttachments(candidateData?.files);
        const nextCandidateInfo = {
          name: candidateData?.personalInformation?.first_name,
          phone: candidateData?.personalInformation?.contact?.phone
        };
        const nextRecruiterOptions = getCandidateRecruitersOptions(candidateData);
        const resolvedRecruiter = !nextRecruiterOptions.length
          ? getDefaultCandidateRecruiter(candidateData)
          : null;

        const nextDuplicateInfoResult = await checkIfCandidateIsAlreadyInSendout({
          candidateId,
          jobOrderId
        });
        const nextDuplicateInfo = {
          ...nextDuplicateInfoResult,
          shouldBlock: disableDuplicateBlocking ? false : nextDuplicateInfoResult.isDuplicate
        };

        if (requestId !== latestRequestIdRef.current) {
          return {
            attachments,
            candidateInfo: nextCandidateInfo,
            candidateDetails: candidateData,
            duplicateInfo: nextDuplicateInfo,
            error: null,
            recruiterOptions: nextRecruiterOptions,
            resolvedRecruiter
          };
        }

        setCandidateAttachments(attachments);
        setRecruiterOptions(nextRecruiterOptions);
        setDuplicateInfo(nextDuplicateInfo);
        setHydratedCandidateId(candidateId);
        onCandidateInfoChange(nextCandidateInfo);
        onDuplicateChange(nextDuplicateInfo);

        return {
          attachments,
          candidateInfo: nextCandidateInfo,
          candidateDetails: candidateData,
          duplicateInfo: nextDuplicateInfo,
          error: null,
          recruiterOptions: nextRecruiterOptions,
          resolvedRecruiter
        };
      } catch (error) {
        if (axios.isCancel(error)) {
          return {
            attachments: [],
            duplicateInfo: getEmptyDuplicateInfo(),
            error: null,
            recruiterOptions: [],
            resolvedRecruiter: null
          };
        }

        const nextDuplicateInfo = {
          ...getEmptyDuplicateInfo(),
          error
        };

        if (requestId !== latestRequestIdRef.current) {
          return {
            attachments: [],
            duplicateInfo: nextDuplicateInfo,
            error,
            recruiterOptions: [],
            resolvedRecruiter: null
          };
        }

        setCandidateAttachments([]);
        setRecruiterOptions([]);
        setDuplicateInfo(nextDuplicateInfo);
        setHydratedCandidateId(null);
        onCandidateInfoChange(null);
        onDuplicateChange(nextDuplicateInfo);
        // Keep generic fetch failures visible; only recruiter-resolution logic moved out.
        showAlert({
          title: strings.shared.errors.serverError.title,
          error: getErrorMessage(error)
        });

        return {
          attachments: [],
          duplicateInfo: nextDuplicateInfo,
          error,
          recruiterOptions: [],
          resolvedRecruiter: null
        };
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsHydratingCandidate(false);
        }
      }
    },
    [abort, jobOrderId, nextSignal, onCandidateInfoChange, onDuplicateChange, showAlert]
  );

  return {
    candidateAttachments,
    duplicateInfo,
    hydratedCandidateId,
    hydrateCandidate,
    isHydratingCandidate,
    recruiterOptions
  };
};
